import { authenticatedApi } from "./client"
import { createResource } from "./resourceFactory"
import {
  QuestsResponse,
  JourneyMission,
  WaitlistResponse,
  JourneysResponse,
  Journey,
  Mission,
} from "@/types/quests"

/**
 * Create resource objects for each API endpoint
 */
const journeysResource = createResource<JourneysResponse>("/journeys/", {
  resourceName: "journeys",
})
const questsResource = createResource<QuestsResponse>("/quests/", {
  resourceName: "quests",
})
const waitlistsResource = createResource<WaitlistResponse>("/waitlists/", {
  resourceName: "questsWaitlist",
})
const journeyMissionResource = createResource<JourneyMission>(
  "/journey_mission",
  { resourceName: "questMission" },
)
const attemptResource = createResource<any>("/attempt", {
  resourceName: "attempt",
})
const missionResource = createResource<Mission>("/mission", {
  resourceName: "mission",
})

/**
 * Endpoint to get journeys
 * @returns Paginated response with available quests
 */
export const getJourneys = async (): Promise<JourneysResponse> => {
  return journeysResource.get()
}

/**
 * Endpoint to get quests
 * @returns Paginated response with available quests
 */
export const getQuests = async (): Promise<QuestsResponse> => {
  return questsResource.get()
}

/**
 * Gets quests in waitlist that the user has already subscribed to
 * @returns Response with waitlisted quests the user has subscribed to
 */
export const getQuestsWaitlist = async (): Promise<WaitlistResponse> => {
  return waitlistsResource.get()
}

/**
 * Interface for question answers
 */
export interface QuestionAnswer {
  question_code: string
  choice_value: number | number[] | string
}

/**
 * Starts a new quest for a specific circle
 * @param circleId ID of the circle to assign the quest to
 * @param questId ID of the quest to start
 * @param answers Optional array of answers to onboarding questions
 * @returns Server response with the created quest
 */
export const startQuest = async (
  circleId: string,
  questId: string,
  answers?: QuestionAnswer[],
): Promise<any> => {
  const circleResource = createResource<Journey | null>(
    `/circle/${circleId}/journey`,
    {
      resourceName: "startQuest",
    },
  )

  // Prepare payload with quest ID and optional answers
  const payload: any = { quest_id: questId }

  // Add answers to the payload if provided
  if (answers && answers.length > 0) {
    payload.answers = answers
  }

  const result = await circleResource.post(payload)

  // The response from the endpoint /circle/{circleId}/journey has a structure
  // where the quest ID is in result.quest.id if it's an available quest.
  // It returns 204 if it's an upcoming quest.
  return result
}

export const unsubscribeFromWaitlist = async (
  waitlistId: string,
): Promise<any> => {
  const result = await waitlistsResource.delete(waitlistId)

  return result
}

/**
 * Starts a mission to indicate it's ready to begin
 * @param missionId ID of the mission to start
 * @returns Response with mission details
 */
export const startJourneyMission = async (
  missionId: string,
): Promise<JourneyMission> => {
  return journeyMissionResource.patch(missionId, {})
}

/**
 * Gets a single journey by ID
 * @param journeyId ID of the quest to retrieve
 * @returns Response with the quest details
 */
export const getSingleJourney = async (journeyId: string): Promise<Journey> => {
  const singleJourneyResource = createResource<Journey>("/journey", {
    resourceName: "singleJourney",
  })
  return singleJourneyResource.getById(journeyId)
}

export interface AcceptJourneyMissionResponse {
  id: string
}

export const acceptJourneyMission = async (
  missionId: string,
): Promise<AcceptJourneyMissionResponse> => {
  const result = await journeyMissionResource.patch(missionId, {
    operation: "accept",
  })

  return result
}

/**
 * Registers an attempt to complete a mission
 * @param attemptId ID of the attempt to complete
 * @param checkJourneyStatus If true, also checks if the quest is completed after completing the mission
 * @returns Server response with updated status
 */
export const completeMissionAttempt = async (
  attemptId: string,
  checkJourneyStatus: boolean = false,
): Promise<any> => {
  // 1. Mark the attempt as completed (PATCH /attempt/:id {operation: succeed})
  console.log(`Marking attempt ${attemptId} as completed`)
  const result = await attemptResource.patch(attemptId, {
    operation: "succeed",
  })

  // The response contains information about the completed attempt
  // including the journey_mission_id we can use to check the stage status
  const journeyMissionId = result?.journey_mission_id

  if (checkJourneyStatus && journeyMissionId) {
    // 2. Get the updated mission status to check if the stage is complete
    try {
      console.log(`Checking status of quest mission ${journeyMissionId}`)
      const missionData = await journeyMissionResource.getById(journeyMissionId)

      // 3. If the mission is completed, check if the stage is complete
      if (missionData && missionData.state === "OK") {
        const stageId = false //missionData.journey_stage_id

        if (stageId) {
          console.log(`Checking if stage ${stageId} is completed`)
          const stageResource = createResource<any>("/journey_stage", {
            resourceName: "questStage",
          })
          const stageData = await stageResource.getById(stageId)

          // 4. If the stage is complete, check if the quest is complete
          if (stageData && stageData.state === "OK") {
            const questId = stageData.journey_id

            if (questId) {
              console.log(`Checking if quest ${questId} is completed`)
              // Get the updated quest status
              await getSingleJourney(questId)
            }
          }
        }
      }
    } catch (statusError) {
      // If there's an error checking the status, log it but don't interrupt the flow
      console.error("Error checking quest status:", statusError)
    }
  }

  return result
}

export const submitMissionQuestionAnswer = async (
  journeyMissionId: string,
  question_code: string,
  answer: string,
): Promise<void> => {
  try {
    await authenticatedApi.patch(
      `/journey_mission/${journeyMissionId}/questions`,
      {
        question_code,
        answer: answer,
      },
    )
  } catch {
    // At this point we don't need to do anything if there's an error.
  }
}

/**
 * Gets a single mission by ID (public endpoint)
 * @param missionId ID of the mission to retrieve
 * @returns Mission data
 */
export const getPublicMission = async (missionId: string): Promise<Mission> => {
  return missionResource.getById(missionId)
}
